import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { showSuccess, showApiError } from "@/utils";
import apiClient from "@/services/apiClient";
import BrandButton from "@/components/ui/BrandButton";
import LanguageSelector from "@/layout/LanguageSelector";
import "@/pages/page.css";
import { AUTH_ISSUER } from "@/config";
import { setRecoveryToken } from "@/services/tokenService";

function VerifyOTP() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const RESEND_COOLDOWN = 60;
  const emailFromState =
    location.state?.email || sessionStorage.getItem("otpEmail") || "";
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(RESEND_COOLDOWN);

  useEffect(() => {
    if (!emailFromState) {
      navigate("/forgot-password", { replace: true });
      return;
    }
    sessionStorage.setItem("otpEmail", emailFromState);
  }, [emailFromState, navigate]);

  useEffect(() => {
    if (resendTimer <= 0) return;
    const id = setTimeout(() => setResendTimer((t) => t - 1), 1000);
    return () => clearTimeout(id);
  }, [resendTimer]);

  const handleResend = async () => {
    setResendLoading(true);
    try {
      const response = await apiClient.post(`${AUTH_ISSUER}/otp-login`, {
        email: emailFromState,
      });
      const data = response.data;
      if (!data || !data.success) {
        showApiError(
          data?.detail ||
            data?.message ||
            t("auth.otpResendFailed", "Failed to send login code. Please try again."),
        );
        return;
      }
      showSuccess(
        t("auth.otpResentSuccess", "A new code has been sent to your email."),
      );
      setResendTimer(RESEND_COOLDOWN);
    } catch (err) {
      showApiError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          t("common.serverError", "Server error. Please try again.")
      );
    } finally {
      setResendLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const response = await apiClient.post(
        `${AUTH_ISSUER}/otp-login/verify`,
        { email: emailFromState, otp }
      );

      const data = response.data;
      const accessToken = data?.access_token;

      if (accessToken || data?.success) {
        if (accessToken) {
          const expiresAt = Date.now() + (data.expires_in || 900) * 1000;
          setRecoveryToken(accessToken, expiresAt);
        }
        sessionStorage.removeItem("otpEmail");
        navigate("/set-new-password", { replace: true });
      } else {
        setError(
          data?.message ||
            t(
              "auth.otpInvalidOrExpired",
              "Invalid or expired code. Please try again.",
            ),
        );
      }
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.response?.data?.message ||
          t(
            "auth.otpInvalidOrExpired",
            "Invalid or expired code. Please try again.",
          )
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container">
      <main className="content" style={{ marginLeft: 0 }}>
        <div className="d-flex justify-content-end p-3">
          <LanguageSelector />
        </div>
        <div className="row justify-content-center mt-3">
          <div className="col-md-5 col-lg-4">
            <div className="card card-radius">
              <div className="card-body">
                <h3 className="text-center mb-2">
                  {t("auth.enterLoginCode", "Enter Login Code")}
                </h3>
                <p className="text-center text-muted mb-4">
                  {t(
                    "auth.otpSentPrompt",
                    {
                      email: emailFromState,
                      defaultValue: `We sent a one-time code to ${emailFromState}. Enter it below to continue.`,
                    },
                  )}
                </p>
                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label htmlFor="otp" className="form-label">
                      {t("auth.oneTimeCode", "One-Time Code")}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="otp"
                      name="otp"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.trim())}
                      required
                      autoComplete="one-time-code"
                      inputMode="numeric"
                      maxLength={12}
                      placeholder={t(
                        "auth.enterCodePlaceholder",
                        "Enter code from email",
                      )}
                    />
                  </div>
                  <BrandButton
                    type="submit"
                    className="w-100"
                    disabled={loading}
                  >
                    {loading
                      ? t("auth.verifying", "Verifying...")
                      : t("auth.verifyCode", "Verify Code")}
                  </BrandButton>
                </form>
                <div className="mt-3 text-center">
                  {resendTimer > 0 ? (
                    <span className="text-muted">
                      {t("auth.resendCodeIn", {
                        count: resendTimer,
                        defaultValue: `Resend code in ${resendTimer}s`,
                      })}
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-link p-0 align-baseline"
                      onClick={handleResend}
                      disabled={resendLoading}
                    >
                      {resendLoading ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-1"
                            role="status"
                            aria-hidden="true"
                          />
                          {t("auth.sending", "Sending...")}
                        </>
                      ) : (
                        t("auth.resendCode", "Resend code")
                      )}
                    </button>
                  )}
                  {" · "}
                  <Link to="/login">
                    {t("auth.backToLogin", "Back to Login")}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

export default VerifyOTP;
